/**
 * element-rules — the SINGLE source of element styling rules, shared by the
 * sandbox preview (slide-sandbox.jsx) and the real output renderer (deck-v2.js)
 * so a slide's element renders IDENTICALLY to how it looks in the catalogue.
 * Pure data + helpers, no React. Changing a rule here changes both.
 */
import { slideSystem } from './slide-render.js';

export const EL_FILL = {
  none: 'transparent', white: '#FFFFFF', 'warm-grey-light': '#FCF8F7', 'warm-grey': '#F3ECE6',
  taupe: '#C9BCB0', black: '#202020', orange: '#FF6428', image: 'linear-gradient(135deg,#C9BCB0,#8a7d72)',
};
export const EL_FILLS = ['none', 'white', 'warm-grey-light', 'warm-grey', 'taupe', 'black', 'orange', 'image'];

// Per-kind defaults. fill/pad/radius describe the element's OWN box; style is the
// brand type style; resize is the behaviour when the frame grows/shrinks.
export const KIND_DEFAULTS = {
  // pad/gap use the master grid: the ONE card/cell padding is 40 (grid.padCard).
  header: { fill: 'none', pad: 0, radius: 0, style: 'title', resize: 'scale' }, // the ONE header block (eyebrow+headline+preamble)
  text:  { fill: 'none', pad: 0, radius: 0, style: 'body', resize: 'scale' },
  list:  { fill: 'none', pad: 0, radius: 0, style: 'body', resize: 'wrap' },
  data:  { fill: 'none', pad: 40, radius: 8, style: 'kpi', resize: 'scale' },
  chart: { fill: 'none', pad: 0, radius: 8, style: 'caption', resize: 'stretch' },
  table: { fill: 'none', pad: 0, radius: 0, style: 'body-sm', resize: 'stretch' },
  card:  { fill: 'white', pad: 40, radius: 8, style: 'body', resize: 'stretch' },
  media: { fill: 'image', pad: 0, radius: 0, style: 'caption', resize: 'cover' },
  shape: { fill: 'warm-grey', pad: 0, radius: 8, style: 'body', resize: 'stretch' },
  diagram: { fill: 'none', pad: 0, radius: 8, style: 'caption', resize: 'stretch' },
  // freeform — a bespoke AI-drawn SVG/HTML illustration (Creator-1-style) that
  // fills its zone; no own box so the illustration owns the surface.
  freeform: { fill: 'none', pad: 0, radius: 0, style: 'caption', resize: 'stretch' },
};
// Per-element type style (so eyebrow ≠ headline ≠ hero ≠ caption).
export const STYLE_FOR_ID = {
  eyebrow: 'eyebrow', kicker: 'eyebrow', metricLabel: 'eyebrow', label: 'label', date: 'caption', annotation: 'caption',
  headline: 'title', title: 'title', keyword: 'title', thesis: 'h3', subhead: 'h3', bigStatement: 'display-section',
  hero: 'display', sectionNumber: 'kpi-sm', bigNumber: 'kpi', number: 'kpi', lede: 'lead', body: 'body', signoff: 'body', cta: 'label',
  caption: 'caption', footnote: 'footnote', attribution: 'footnote', quote: 'quote', pullQuote: 'quote',
};
// Per-element fill override (KPI tiles get a white card; bare stats sit on the
// ground). White cards on a warm/cream ground are the approved-deck pattern.
export const FILL_FOR_ID = {
  kpi: 'white', statCard: 'white', metricTile: 'white', numberBox: 'white', scorecard: 'white',
  rectangle: 'warm-grey', roundedRect: 'warm-grey', panel: 'warm-grey-light', card: 'white',
};

// SIZE ENVELOPE — every element declares min / default / max for width, height
// and sub-element count ([min, default, max]); the behaviour rule (resize) governs
// what happens IN BETWEEN. Per-kind defaults, overridable per element via
// def.rules.size. Units are the editor preview's pixels.
export const SIZE_DEFAULTS = {
  text:    { w: [120, 360, 460], h: [40, 110, 280], items: [1, 1, 1] },
  header:  { w: [240, 460, 460], h: [80, 200, 320], items: [1, 1, 1] },
  list:    { w: [160, 380, 460], h: [60, 200, 320], items: [1, 4, 8] },
  data:    { w: [120, 260, 380], h: [80, 200, 320], items: [1, 1, 1] },
  chart:   { w: [160, 380, 460], h: [100, 220, 320], items: [2, 5, 9] },
  table:   { w: [220, 460, 460], h: [100, 220, 320], items: [1, 4, 8] },
  card:    { w: [160, 320, 460], h: [120, 220, 320], items: [1, 3, 6] },
  media:   { w: [120, 380, 460], h: [90, 220, 320], items: [1, 1, 1] },
  shape:   { w: [80, 200, 460], h: [40, 120, 320], items: [1, 1, 1] },
  diagram: { w: [220, 460, 460], h: [110, 220, 320], items: [2, 4, 6] },
  freeform: { w: [220, 460, 460], h: [140, 280, 360], items: [1, 1, 1] },
};
export function elementSize(def) {
  const base = SIZE_DEFAULTS[def.kind] || SIZE_DEFAULTS.shape;
  const idov = sizeForId(def.id || '');
  const o = (def.rules && def.rules.size) || {};
  return { w: o.w || idov.w || base.w, h: o.h || idov.h || base.h, items: o.items || idov.items || base.items };
}
// Per-element TAILORED envelopes (regex on the id) layered over the kind default —
// a small eyebrow, a square donut, a thin divider and a tall quote each get a
// sensible [min, default, max] for width / height / items.
function sizeForId(id) {
  if (/eyebrow|kicker|^label$|metricLabel|^date$|caption|footnote|annotation|attribution|^tag$/.test(id)) return { h: [40, 60, 120] };
  if (/hero|bigStatement|sectionNumber/.test(id)) return { w: [300, 460, 460], h: [120, 260, 360] };
  if (/quote/.test(id)) return { w: [200, 440, 460], h: [120, 240, 360] };
  if (/accentRule|^divider$/.test(id)) return { w: [80, 200, 460], h: [8, 12, 40] };
  if (/timeline|sparkline|logoStrip|statRow|iconRow|progress|chevron|processArrows|^arrow$/.test(id)) return { h: [40, 100, 200] };
  if (/pie|donut|gauge|radar/.test(id)) return { w: [140, 260, 360], h: [140, 240, 360] };
  if (/pill|badge/.test(id)) return { w: [80, 140, 300], h: [32, 48, 90] };
  if (/numberSquare|^qr$|avatar|^icon$/.test(id)) return { w: [80, 160, 260], h: [80, 160, 260] };
  if (/swot|twoByTwo|matrix|venn|cycle|hubSpoke|^hub|pyramid|orgChart/.test(id)) return { w: [200, 380, 460], h: [180, 300, 320] };
  if (/kpi|statCard|metricTile|numberBox|scorecard/.test(id)) return { w: [180, 320, 460], h: [140, 220, 320] };
  return {};
}

export function elementRules(def) {
  const base = KIND_DEFAULTS[def.kind] || KIND_DEFAULTS.shape;
  const sid = STYLE_FOR_ID[def.id];
  const fid = FILL_FOR_ID[def.id];
  return { ...base, ...(sid ? { style: sid } : {}), ...(fid ? { fill: fid } : {}), ...(def.rules || {}) };
}

export function elementDef(id) {
  const e = slideSystem.elementCatalog?.elements?.[id];
  return e ? { ...e, id } : { id, label: id, cat: 'Text', kind: 'text' };
}
